import { useState, FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { PasswordField, FormError, FormSuccess, SubmitButton } from '@/components/auth/FormFields';
import { authApi, readApiError } from '@/lib/auth-api';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const nav = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (password.length < 8) return setErr(t('auth.error_password_short'));
    if (password !== confirm) return setErr(t('auth.error_password_mismatch'));
    setPending(true);
    try {
      await authApi.resetPassword(token, password);
      setOk(t('auth.reset_success'));
      setTimeout(() => nav('/login', { replace: true }), 1400);
    } catch (e) {
      setErr(readApiError(e, t('auth.error_reset_failed')));
    } finally {
      setPending(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title={t('auth.reset_missing_token_title')}>
        <p className="text-[14px] text-muted-foreground leading-relaxed">
          {t('auth.reset_missing_token_body')}
        </p>
        <Link
          to="/forgot-password"
          className="mt-6 inline-flex h-11 items-center justify-center px-5 rounded-md bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 transition-colors"
        >
          {t('auth.request_new_link')}
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.reset_title')} subtitle={t('auth.reset_subtitle')}>
      <form onSubmit={onSubmit} className="space-y-4">
        <FormError message={err} />
        <FormSuccess message={ok} />
        <PasswordField
          id="password"
          label={t('auth.new_password_label')}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          showStrength
        />
        <PasswordField
          id="confirm"
          label={t('auth.confirm_password_label')}
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
        />
        <SubmitButton pending={pending}>{t('auth.set_new_password')}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
